// Boutique et loterie des challenges (spec 2026-10-16) : conversions formulaire ↔ API et contrôles
// avant le PUT /challenge (qui REMPLACE le challenge : tout champ non renvoyé est effacé).

/** Minuit, heure de Paris, du jour YYYY-MM-DD, en ISO UTC (gère l'heure d'été). */
export function parisMidnightISO(date) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || "")) return "";
  const [y, m, d] = date.split("-").map(Number);
  // décalage de Paris ce jour-là : on lit l'heure locale de Paris d'un instant UTC proche de minuit
  const probe = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
  const parisHour = Number(new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Paris", hour: "2-digit", hourCycle: "h23" }).format(probe));
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0) - parisHour * 3600000).toISOString();
}

function parisDate(iso) {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime()) || t.getUTCFullYear() < 2000) return "";
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" }).format(t);
}

const two = (m) => ({ fr: (m || {}).fr || "", es: (m || {}).es || "" });
const clean = (m) => ({ ...(m.fr.trim() ? { fr: m.fr.trim() } : {}), ...(m.es.trim() ? { es: m.es.trim() } : {}) });

/** État du formulaire depuis la loterie renvoyée par l'API (null/absente ⇒ vide). */
export function lotteryForm(lottery) {
  const l = lottery || {};
  return {
    opensAt: parisDate(l.opensAt),
    ticketPriceMiles: l.ticketPriceMiles || 0,
    ticketPriceCoins: l.ticketPriceCoins || 0,
    maxTicketsPerDraw: l.maxTicketsPerDraw || 0,
    draws: (l.draws || []).map(d => ({
      date: d.date || "",
      cutoffHour: d.cutoffHour || 21,
      prizes: (d.prizes || []).map(p => ({
        id: p.id || "", label: two(p.label), description: two(p.description), image: p.image || "",
        value: p.value || "", winners: p.winners === undefined ? 1 : p.winners, basket: p.kind === "basket",
      })),
    })),
  };
}

/** Corps `lottery` du PUT /challenge. */
export function lotteryBody(form) {
  return {
    opensAt: parisMidnightISO(form.opensAt),
    ticketPriceMiles: parseInt(form.ticketPriceMiles, 10) || 0,
    ticketPriceCoins: parseInt(form.ticketPriceCoins, 10) || 0,
    maxTicketsPerDraw: parseInt(form.maxTicketsPerDraw, 10) || 0,
    draws: form.draws.map(d => ({
      date: d.date,
      cutoffHour: parseInt(d.cutoffHour, 10) || 21,
      prizes: d.prizes.map(p => ({
        id: p.id.trim(), label: clean(p.label), description: clean(p.description), image: p.image || "",
        value: (p.value || "").trim(), winners: parseInt(p.winners, 10) || 0, kind: p.basket ? "basket" : "",
      })),
    })),
  };
}

/** Message d'erreur lisible, ou null si la loterie est valide (aucun tirage = rien à valider). */
export function validateLottery(form) {
  if (!form.draws.length) return null;
  if (!form.opensAt) return "Loterie : date d'ouverture obligatoire";
  if (!(parseInt(form.ticketPriceMiles, 10) > 0)) return "Loterie : prix du ticket en miles obligatoire (> 0)";
  const dates = {};
  for (const [i, d] of form.draws.entries()) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d.date) || dates[d.date]) return `Tirage ${i + 1} : date invalide ou en double`;
    dates[d.date] = true;
    const h = parseInt(d.cutoffHour, 10);
    if (Number.isNaN(h) || h < 0 || h > 23) return `Tirage ${i + 1} : heure de clôture entre 0 et 23`;
    if (!d.prizes.length) return `Tirage ${i + 1} : au moins un lot`;
    const ids = {};
    for (const [j, p] of d.prizes.entries()) {
      if (!/^[a-z0-9-]+$/.test(p.id.trim()) || ids[p.id.trim()]) return `Tirage ${i + 1}, lot ${j + 1} : identifiant (minuscules, chiffres, tirets) manquant ou en double`;
      ids[p.id.trim()] = true;
      if (!p.label.fr.trim()) return `Tirage ${i + 1}, lot ${j + 1} : libellé FR obligatoire`;
      if (!(parseInt(p.winners, 10) >= 1)) return `Tirage ${i + 1}, lot ${j + 1} : au moins 1 gagnant`;
    }
  }
  return null;
}

/** CSV des gagnants publiés (gagnants remplacés exclus), « ; » + BOM pour Excel. */
export function winnersCSV(draws, prizeLabels) {
  const replaced = new Set((draws || []).filter(r => r.replacedUserID).map(r => `${r.date}|${r.replacedUserID}`));
  const rows = [["date", "lot", "gagnant", "email", "tickets"]];
  for (const r of [...(draws || [])].sort((a, b) => (a.date + a.seq).localeCompare(b.date + b.seq))) {
    for (const w of r.winners || []) {
      if (w.userID && replaced.has(`${r.date}|${w.userID}`)) continue;
      rows.push([r.date, prizeLabels[w.prizeId] || w.prizeId, w.displayName || "", w.email || "", String(w.tickets || 0)]);
    }
  }
  return "﻿" + rows.map(cols => cols.map(c => String(c).replace(/[;\n\r]/g, " ")).join(";")).join("\n");
}
